import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

/**
 * Haul.Taxi's Privacy Policy, owned in CODE rather than in a Console content asset — the same
 * code-ownership rationale already used for the landing page and the footer.
 *
 * Why: the hosted `content/pages/privacy-policy.json` asset shipped with Sharetribe's stock
 * placeholder ("Every marketplace business needs a privacy policy… use our template as a basis"),
 * and that placeholder was live in production.
 *
 * ⚠️ NOT LEGAL ADVICE, AND NOT COUNSEL-REVIEWED. The subprocessor list and the data-handling
 * descriptions below were written to match what the code actually does today (Stripe, Cloudflare R2,
 * Sharetribe, SendGrid, Mapbox, Render, Sentry, Anthropic). If you add, remove, or change a
 * subprocessor, update this file in the same change. The long-form draft staged for an attorney is
 * `docs/legal/privacy-policy.md`.
 */

const LAST_UPDATED = 'August 13, 2026';

const privacyMarkdown = `
_Last updated: ${LAST_UPDATED}_

This policy explains what Haul.Taxi collects, why, who we share it with, and the choices you have. It
applies to the Haul.Taxi website and web app. See also our [Terms of Service](/terms-of-service).

## 1. Information you give us

- **Account details** — name, email address, and password. Haulers also provide vehicle details
  (type, make/model, licence plate) and insurance details (provider and policy number).
- **Job details** — pickup address, the photos you upload, and any optional details you choose to
  add (item categories, load size, access notes, budget).
- **Messages and bids** exchanged through the Platform.
- **Verification documents** — Haulers upload insurance certificates and vehicle documents for review.
- **Reviews** you write.

## 2. Information collected automatically

Basic technical data such as IP address, browser and device type, pages viewed, and timestamps. We use
cookies and similar technologies to keep you signed in, remember preferences, and keep the service
secure. A session cookie is required for the site to work.

## 3. Location

We geocode the **addresses you type** so jobs can be placed on a map and matched to nearby Haulers.
**We do not collect continuous or precise device GPS location.**

## 4. Payments and identity

Payments are handled by **Stripe**. Card details go directly to Stripe — **we never receive or store
full card numbers.** Haulers who take payouts complete Stripe Connect onboarding, which may require
identity and bank information supplied directly to Stripe under Stripe's own privacy policy.

Where a Hauler completes identity verification, the government ID and selfie are processed by **Stripe
Identity**. Those images **never reach Haul.Taxi's servers** — we store only the resulting
verification status.

## 5. Photos and AI job scoping

When you post a job with photos, the **photos and job details you submit are sent to our AI provider
(Anthropic)** so we can describe the job and work out what equipment it needs, in order to route it
to suitable Haulers. The result is stored on your job listing and is visible to Haulers browsing the
marketplace. Please do not upload photos containing documents, identification, or other sensitive
personal information — upload pictures of the items to be hauled.

## 6. How we use your information

To operate the marketplace (publish jobs, deliver bids, enable a Customer to choose a Hauler), process
payments, verify Haulers, provide support, send service email about your account and transactions,
detect fraud and abuse, meet legal obligations, and improve the product.

## 7. When we share information

- **With the other party to a job.** When a Customer accepts a bid, the Customer and Hauler see the
  information needed to complete the job — including the **pickup address** and contact details. Your
  full address is not shown publicly before a bid is accepted.
- **Publicly.** Your display name, profile photo, reviews, and job listing content (including item
  photos) may be visible to other users and to visitors.
- **With service providers** who process data on our behalf, listed below.
- **For legal reasons** — to comply with law, enforce our Terms, or protect the rights and safety of
  users and the public.
- **In a business transfer** — if the business is sold or reorganised, subject to this policy.

**We do not sell your personal information.**

## 8. Service providers

| Provider | What it does for us |
|---|---|
| Sharetribe | Marketplace backend (accounts, listings, transactions) |
| Stripe | Payments, payouts, and identity verification |
| Cloudflare R2 | Storage of hauler verification documents |
| Anthropic | AI job scoping from submitted photos and job details |
| Mapbox | Maps and address geocoding |
| SendGrid | Transactional email |
| Render | Application hosting |
| Sentry | Error monitoring (may include a user identifier) |

## 9. Security

Verification documents are stored encrypted at rest and are accessible only to the Hauler who uploaded
them and to authorised Haul.Taxi reviewers, through short-lived links. Access to production systems is
restricted. No system is perfectly secure, so we cannot guarantee absolute security.

## 10. Retention and deletion

We keep information for as long as your account is active and as needed for the purposes above,
including legal, tax, accounting, and dispute-resolution requirements.

You can delete your account from **Account settings → Manage account**. When you do, we remove your
profile, delete your stored verification documents, and remove saved job-address details. Some records
— such as transaction and payment records held by us or by Stripe — are retained where the law
requires. Content already shared with a counterparty, and reviews, may persist.

## 11. Your choices and rights

You can view and update your profile and account details at any time, and delete your account as
above. Depending on where you live, you may have rights to access, correct, delete, or receive a copy
of your personal information, to object to or restrict certain processing, and to be free from
discrimination for exercising those rights. To make a request, contact us using the details below; we
may need to verify your identity first.

## 12. Children

The Platform is not intended for anyone under 18 and we do not knowingly collect information from
children. If you believe a child has given us information, contact us and we will delete it.

## 13. Changes

We will update this policy as the service changes and will revise the "last updated" date above.
Material changes will be communicated through the Platform.

## 14. Contact

Questions, or to make a privacy request: **haultaxi@gmail.com**
`;

// Create the page content (array of sections) in page-asset format.
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'privacy',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Privacy Policy' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'privacy-content',
          text: {
            fieldType: 'markdown',
            content: privacyMarkdown,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Privacy Policy | Haul.Taxi',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content:
        'What Haul.Taxi collects, how it is used, who it is shared with, and the choices you have.',
    },
  },
};

const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
